import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridOptions, ValueFormatterParams } from 'ag-grid-community';

import { DeviceClaimProgress, DeviceDetection } from '@shure/cloud/shared/models/devices';
import { BaseTableComponent } from '@shure/cloud/shared/ui/components';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import { compareStrings } from '@shure/shared/angular/ui/comparators/string';
import {
	AgGridResponsiveWrapperComponent,
	areDeviceNameCellsEqual,
	DeviceNameCellData,
	DeviceNameRendererComponent,
	DeviceNameRendererParams,
	makeComparator,
	ProgressRendererComponent,
	ProgressStatus
} from '@shure/shared/angular/ui/components/ag-grid-next';

import {
	ClaimStatusCellData,
	claimStatusCellDataComparator,
	claimStatusKeyCreator
} from '../../../../../models/claim-status-cell-data';
import {
	mapDevicesToClaimDataToLocalNetworkConfirmationTableRowData,
	LocalNetworkConfirmationTableRowData
} from '../../../../../models/local-network-confirmation-row-data';
import { mapProgressStatusToTranslocoKey } from '../../../../../models/mappers/map-progress-status-to-transloco-key';

interface LocalNetworkConfirmationTableContext {
	componentParent: LocalNetworkConfirmationTableComponent;
}

@Component({
	selector: 'sh-local-network-confirmation-table',
	imports: [AgGridAngular, AgGridResponsiveWrapperComponent, PipesModule],
	templateUrl: './local-network-confirmation-table.component.html',
	styleUrls: ['./local-network-confirmation-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocalNetworkConfirmationTableComponent extends BaseTableComponent<LocalNetworkConfirmationTableRowData> {
	@Input()
	public set devices(_devices: DeviceClaimProgress<DeviceDetection>['devices'][0][] | null) {
		this.gridData = mapDevicesToClaimDataToLocalNetworkConfirmationTableRowData(_devices ?? []);
	}

	// AG Grid Members
	public gridData: LocalNetworkConfirmationTableRowData[] = [];

	public context: LocalNetworkConfirmationTableContext = {
		componentParent: this
	};

	public readonly columnDefs: ColDef<LocalNetworkConfirmationTableRowData>[] = [
		//
		// Device Name: ICON, Name
		//
		this.createCustomRendererColumn({
			field: 'nameCellData',
			headerName: this.translocoService.translate('common.labels.device-name'),
			cellRenderer: DeviceNameRendererComponent,
			cellRendererParams: {
				shouldHideStateBar: true,
				renderDeviceNameAsText: true
			} satisfies DeviceNameRendererParams,
			cellClass: 'sh-device-name-cell-renderer',
			lockVisible: true,
			filter: true,
			filterValueGetter: (params): string | undefined => params.data?.nameCellData.name.name,
			equals: areDeviceNameCellsEqual,
			comparator: makeComparator<DeviceNameCellData>((a, b) => compareStrings(a?.name.name, b?.name.name)),
			getQuickFilterText: (params) => params.data.nameCellData.name.name.toLowerCase(),
			valueParser: (params): string | undefined => params.data.nameCellData.name.name,
			minWidth: 250
		}),
		//
		// Model
		//
		this.createTextColumn({
			field: 'model',
			headerName: this.translocoService.translate('common.labels.device-model')
		}),
		//
		// IP Address
		//
		this.createIpAddressColumn({
			field: 'ipAddress'
		}),
		//
		// Firmware
		//
		this.createTextColumn({
			field: 'firmwareVersion',
			headerName: this.translocoService.translate('common.labels.firmware-version')
		}),
		//
		// MAC Address
		//
		this.createMacAddressColumn({
			field: 'macAddress'
		}),
		//
		// status
		//
		this.createCustomRendererColumn({
			field: 'claimStatusCellData',
			headerName: this.translocoService.translate('cloud.add-devices-dialog.status'),
			cellRenderer: ProgressRendererComponent,
			cellClass: 'sh-claim-status-cell-renderer',
			filter: 'agSetColumnFilter',
			filterParams: {
				comparator: claimStatusCellDataComparator,
				valueFormatter: (params: ValueFormatterParams<unknown, ClaimStatusCellData>) =>
					this.translocoService.translate(
						mapProgressStatusToTranslocoKey(params.value?.status ?? ProgressStatus.InProgress)
					)
			},
			keyCreator: claimStatusKeyCreator,
			comparator: claimStatusCellDataComparator
		})
	];

	constructor(private readonly translocoService: TranslocoService) {
		super(translocoService);
		this.context.componentParent = this;
	}

	public getRowId(params: GetRowIdParams<LocalNetworkConfirmationTableRowData>): string {
		return params.data.id;
	}

	protected override getGridOptions(): GridOptions<LocalNetworkConfirmationTableRowData> {
		return {
			...super.getGridOptions(),
			// Attaching the AG Grid popups to the document body currently does not work with Prism dialogs.
			// For now we'll attach to the grid and take the hit on menus getting cut off on smaller tables.
			// TODO: remove this once Prism dialogs have better AG Grid support
			popupParent: undefined
		};
	}
}
